// 核心任务管理 API
import { createAppRequest } from '@/services/request';
import type { TaskCompletionUndo } from './type';

const request = createAppRequest();

import type {
  CreateTaskDto,
  CreateTaskListDto,
  DashboardStatistics,
  Task,
  TaskActivity,
  TaskAttachment,
  TaskCompletionSmartAnalysisResult,
  TaskDeletePreview,
  TaskDeleteStrategy,
  TaskDependencyEdge,
  TaskDependencyGraph,
  TaskDependencyType,
  TaskList,
  TaskListType,
  TaskNavPendingCounts,
  TaskQueryParams,
  TaskStartability,
  TaskStatistics,
  TaskStatus,
  TaskUnifiedQueryParams,
  TaskUnifiedQueryResponse,
  UpdateTaskDto,
  UpdateTaskListDto,
} from './type';

// ==================== 任务管理 API ====================

// 核心任务管理 API
export const taskApi = {
  /**
   * 获取任务详情
   */
  getTaskById: (uuid: string) =>
    request<Task>(`/bff/tasks/${uuid}`, {
      method: 'GET',
    }),

  /**
   * 创建任务
   */
  createTask: (data: CreateTaskDto) =>
    request<Task>('/bff/tasks', {
      method: 'POST',
      data,
    }),

  /**
   * 更新任务
   */
  updateTask: (uuid: string, data: UpdateTaskDto) =>
    request<Task>(`/bff/tasks/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  /**
   * 删除任务
   */
  deleteTask: (uuid: string) =>
    request<void>(`/bff/tasks/${uuid}`, {
      method: 'DELETE',
    }),

  /**
   * 更新任务完成状态
   */
  updateTaskCompletion: (
    uuid: string,
    isCompleted: boolean,
    undo?: TaskCompletionUndo,
  ) =>
    request<Task>(`/bff/tasks/${uuid}/completion`, {
      method: 'PATCH',
      data: { isCompleted, ...undo },
    }),

  /**
   * 更新任务进度
   */
  updateTaskProgress: (uuid: string, progress: number) =>
    request<Task>(`/bff/tasks/${uuid}/progress`, {
      method: 'PATCH',
      data: { progress },
    }),

  /**
   * 获取任务统计信息
   */
  getTaskStatistics: () =>
    request<TaskStatistics>('/bff/tasks/statistics', {
      method: 'GET',
    }),

  getDashboardStatistics: () =>
    request<DashboardStatistics>('/bff/tasks/dashboard-statistics', {
      method: 'GET',
    }),

  // 获取菜单项统计
  getMenuStatistics: () =>
    request<any>('/bff/tasks/menu-statistics', {
      method: 'GET',
    }),

  // 获取统一统计信息（合并基础统计和菜单统计）
  getUnifiedStatistics: () =>
    request<any>('/bff/tasks/unified-statistics', {
      method: 'GET',
    }),

  // 获取左侧导航未完成任务统计
  getNavPendingCounts: () =>
    request<TaskNavPendingCounts>('/bff/tasks/nav-pending-counts', {
      method: 'GET',
    }),

  /**
   * 获取子任务列表
   */
  getSubtasks: (taskUuid: string) =>
    request<Task[]>(`/bff/tasks/${taskUuid}/subtasks`, {
      method: 'GET',
    }),

  /**
   * 添加子任务
   */
  addSubtask: (
    taskUuid: string,
    data: {
      title: string;
      description?: string;
      sortWeight?: number;
    },
  ) =>
    request<Task>(`/bff/tasks/${taskUuid}/subtasks`, {
      method: 'POST',
      data,
    }),

  // 更新子任务
  updateSubtask: (
    subtaskUuid: string,
    data: {
      title?: string;
      content?: string;
      status?: TaskStatus;
      sortWeight?: number;
    },
  ) =>
    request<Task>(`/bff/tasks/subtasks/${subtaskUuid}`, {
      method: 'PATCH',
      data,
    }),

  updateSubtaskCompletion: (
    subtaskUuid: string,
    isCompleted: boolean,
    undo?: TaskCompletionUndo,
  ) =>
    request<Task>(`/bff/tasks/subtasks/${subtaskUuid}/completion`, {
      method: 'PATCH',
      data: { isCompleted, ...undo },
    }),

  // 删除子任务
  deleteSubtask: (subtaskUuid: string) =>
    request<void>(`/bff/tasks/subtasks/${subtaskUuid}`, {
      method: 'DELETE',
    }),

  // 添加任务附件
  addAttachment: (
    taskUuid: string,
    data: {
      name: string;
      originalName: string;
      filePath: string;
      fileSize: number;
      mimeType: string;
      extension: string;
      description?: string;
      isImage?: boolean;
      thumbnailPath?: string;
    },
  ) =>
    request<TaskAttachment>(`/bff/tasks/${taskUuid}/attachments`, {
      method: 'POST',
      data,
    }),

  // 通过后端文件管理 SDK 上传任务附件
  uploadAttachmentFile: (taskUuid: string, data: FormData) =>
    request<TaskAttachment>(`/bff/tasks/${taskUuid}/attachments/upload`, {
      method: 'POST',
      data,
    }),

  // 创建任务附件上传签名 URL
  createAttachmentUploadUrl: (
    taskUuid: string,
    data: {
      originalName: string;
      contentLength: number;
    },
  ) =>
    request<{ uploadUrl: string; objectKey: string }>(
      `/bff/tasks/${taskUuid}/attachments/upload-url`,
      {
        method: 'POST',
        data,
      },
    ),

  // 获取任务附件列表
  getTaskAttachments: (taskUuid: string) =>
    request<TaskAttachment[]>(`/bff/tasks/${taskUuid}/attachments`, {
      method: 'GET',
    }),

  // 删除任务附件
  deleteAttachment: (attachmentUuid: string) =>
    request<void>(`/bff/tasks/attachments/${attachmentUuid}`, {
      method: 'DELETE',
    }),

  // 创建任务附件下载签名 URL
  createAttachmentDownloadUrl: (attachmentUuid: string) =>
    request<{ url: string }>(
      `/bff/tasks/attachments/${attachmentUuid}/download-url`,
      {
        method: 'POST',
      },
    ),

  // 关注任务
  watchTask: (taskId: string) =>
    request<void>(`/bff/tasks/${taskId}/watch`, {
      method: 'POST',
    }),

  // 取消关注任务
  unwatchTask: (taskId: string) =>
    request<void>(`/bff/tasks/${taskId}/watch`, {
      method: 'DELETE',
    }),

  // 获取任务动态列表
  getTaskActivities: (params?: {
    page?: number;
    limit?: number;
    taskId?: string;
    activityType?: string;
    actorMemberId?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) {
      searchParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.taskId) {
      searchParams.append('taskId', params.taskId);
    }
    if (params?.activityType) {
      searchParams.append('activityType', params.activityType);
    }
    if (params?.actorMemberId) {
      searchParams.append('actorMemberId', params.actorMemberId);
    }

    return request<{ activities: TaskActivity[]; total: number }>(
      `/bff/tasks/activities?${searchParams.toString()}`,
      {
        method: 'GET',
      },
    );
  },

  getMyTaskActivities: (page = 1, limit = 10, activityType = '') =>
    request<{ activities: TaskActivity[]; total: number }>(
      `/bff/tasks/my/activities?${new URLSearchParams({
        page: String(page),
        limit: String(limit),
        ...(activityType ? { activityType } : {}),
      }).toString()}`,
      {
        method: 'GET',
      },
    ),

  // 获取任务依赖图（当前任务 + 前置 + 后置）
  getTaskDependencyGraph: (taskUuid: string) =>
    request<TaskDependencyGraph>(`/bff/tasks/${taskUuid}/dependencies/graph`, {
      method: 'GET',
    }),

  // 新增任务依赖
  addTaskDependency: (
    taskUuid: string,
    data: {
      predecessorTaskUuid: string;
      dependencyType: TaskDependencyType;
    },
  ) =>
    request<TaskDependencyEdge>(`/bff/tasks/${taskUuid}/dependencies`, {
      method: 'POST',
      data,
    }),

  // 删除单条依赖
  deleteTaskDependency: (taskUuid: string, dependencyUuid: string) =>
    request<void>(`/bff/tasks/${taskUuid}/dependencies/${dependencyUuid}`, {
      method: 'DELETE',
    }),

  // 任务可启动性（强弱依赖校验）
  getTaskStartability: (taskUuid: string) =>
    request<TaskStartability>(`/bff/tasks/${taskUuid}/startability`, {
      method: 'GET',
    }),

  // AI 分析任务完成情况（SMART）
  analyzeTaskCompletionSmart: (taskUuid: string) =>
    request<TaskCompletionSmartAnalysisResult>(
      `/bff/tasks/${taskUuid}/analysis/completion-smart`,
      {
        method: 'POST',
        data: {},
      },
    ),

  // 删除前预览影响范围（子任务 + 依赖）
  getTaskDeletePreview: (taskUuid: string) =>
    request<TaskDeletePreview>(`/bff/tasks/${taskUuid}/delete-preview`, {
      method: 'GET',
    }),
};

// 任务清单管理 API
export const taskListApi = {
  // 获取任务清单列表
  getTaskLists: (params?: {
    page?: number;
    limit?: number;
    type?: TaskListType;
    creatorId?: string;
    isDefault?: boolean;
    isPublic?: boolean;
    sourceAppUuid?: string;
    sourceSceneUuid?: string;
    sourceObjectUuid?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) {
      searchParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.type) {
      searchParams.append('type', params.type);
    }
    if (params?.creatorId) {
      searchParams.append('creatorId', params.creatorId);
    }
    if (params?.isDefault !== undefined) {
      searchParams.append('isDefault', params.isDefault.toString());
    }
    if (params?.isPublic !== undefined) {
      searchParams.append('isPublic', params.isPublic.toString());
    }
    if (params?.sourceAppUuid) {
      searchParams.append('sourceAppUuid', params.sourceAppUuid);
    }
    if (params?.sourceSceneUuid) {
      searchParams.append('sourceSceneUuid', params.sourceSceneUuid);
    }
    if (params?.sourceObjectUuid) {
      searchParams.append('sourceObjectUuid', params.sourceObjectUuid);
    }

    return request<{ taskLists: TaskList[]; total: number }>(
      `/bff/tasks/lists?${searchParams.toString()}`,
      {
        method: 'GET',
      },
    );
  },

  // 获取任务清单详情
  getTaskListById: (uuid: string) =>
    request<TaskList>(`/bff/tasks/lists/${uuid}`, {
      method: 'GET',
    }),

  // 创建任务清单
  createTaskList: (data: CreateTaskListDto) =>
    request<TaskList>('/bff/tasks/lists', {
      method: 'POST',
      data,
    }),

  // 更新任务清单
  updateTaskList: (uuid: string, data: UpdateTaskListDto) =>
    request<TaskList>(`/bff/tasks/lists/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  // 删除任务清单
  deleteTaskList: (uuid: string) =>
    request<void>(`/bff/tasks/lists/${uuid}`, {
      method: 'DELETE',
    }),

  // 分享任务清单
  shareTaskList: (uuid: string, sharedMemberUuids: string[]) =>
    request<TaskList>(`/bff/tasks/lists/${uuid}/share`, {
      method: 'PATCH',
      data: {
        sharedMemberUuids,
      },
    }),

  // 获取动态任务清单的任务
  getDynamicTaskListTasks: (uuid: string) =>
    request<Task[]>(`/bff/tasks/lists/${uuid}/dynamic-tasks`, {
      method: 'GET',
    }),

  // 获取任务清单下的任务列表
  getTaskListTasks: ({
    uuid,
    params,
  }: {
    uuid: string;
    params?: TaskQueryParams;
  }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach((v) => {
              searchParams.append(key, String(v));
            });
          } else {
            searchParams.append(key, String(value));
          }
        }
      });
    }
    return request<{ tasks: Task[]; total: number }>(
      `/bff/tasks/lists/${uuid}/tasks?${searchParams.toString()}`,
      {
        method: 'GET',
      },
    );
  },
};

export const getTasksNewApi = (params: TaskUnifiedQueryParams) => {
  return request<TaskUnifiedQueryResponse>('/bff/tasks/new/query', {
    method: 'POST',
    data: params,
  });
};
